import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { authAPI } from "../../services/api";
import "./style.scss";

function SignIn({ isEmployerLogin = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [showPass, setShowPass] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!formData.email || !formData.password) {
      setError("Please enter your email and password");
      setLoading(false);
      return;
    }

    try {
      // Call login API
      const response = await authAPI.login({
        email: formData.email,
        password: formData.password
      });

      if (response.data.success) {
        // Login user
        const { user, token } = response.data.data;
        login(user, token);

        // Redirect to dashboard or return URL
        const from = location.state?.from || "/dashboard";
        navigate(from, { replace: true });
      } else {
        setError(response.data.message || "Login failed");
      }
    } catch (err) {
      let errorMessage = err.response?.data?.message || "Invalid email or password";
      
      // If email not verified, show resend option
      if (err.response?.data?.requiresVerification) {
        setRequiresVerification(true);
        errorMessage = err.response.data.message;
      } else {
        setRequiresVerification(false);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setError("Please enter your email address");
      return;
    }

    setResending(true);
    setError("");
    setResendSuccess(false);

    try {
      const response = await authAPI.resendVerification(formData.email);
      if (response.data.success) {
        setResendSuccess(true);
        setError("");
      } else {
        setError(response.data.message || "Failed to resend verification email");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend verification email");
    } finally {
      setResending(false);
    }
  };

  return (
    <section className="account-section">
      <div className="tf-container">
        <div className="row">
          <div className="wd-form-login">
            <div className="login-header-text">
              <h4>Welcome back to Talsolution</h4>
              <p>Log in to manage your applications and messages.</p>
            </div>
            {error && (
              <div className={`alert ${requiresVerification ? "alert-warning" : "alert-danger"}`}>
                {error}
                {requiresVerification && (
                  <div className="verification-resend-section">
                    {resendSuccess ? (
                      <div className="resend-success">
                        ✓ Verification email sent! Please check your inbox.
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="resend-verification-btn"
                        onClick={handleResendVerification}
                        disabled={resending}
                      >
                        {resending ? "Sending..." : "Resend Verification Email"}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="ip">
                <label>
                  Email address<span>*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="your.email@example.com"
                  required
                />
              </div>
              <div className="ip">
                <label>
                  Password<span>*</span>
                </label>
                <div className="inputs-group auth-pass-inputgroup">
                  <input
                    type={showPass ? "text" : "password"}
                    className="input-form password-input"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Password"
                    id="password-input"
                    required
                  />
                  <span
                    className={`password-addon ${
                      showPass ? "icon-eye" : "icon-eye-off"
                    }`}
                    id="password-addon"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowPass(!showPass);
                    }}
                  />
                </div>
              </div>
              <div className="group-ant-choice">
                <div className="sub-ip">
                  <input type="checkbox" />
                  Remember me
                </div>
                <Link to="/forgot-password" className="forgot">
                  Forgot password?
                </Link>
              </div>
              <button type="submit" disabled={loading}>
                {loading ? "Logging in..." : "Login"}
              </button>
              <div className="sign-up">
                Not registered yet? <Link to="/register">Register</Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default SignIn;
